import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, MapPin, Car, Wrench, CreditCard, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const problems = [
  "Não gela / gela pouco",
  "Cheiro ruim",
  "Barulho estranho",
  "Vazamento visível",
  "Parou de funcionar",
  "Outro (descrever)",
];

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Booking() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const [services, setServices] = useState([]);
  const [fees, setFees] = useState({ travel_fee: 0, diagnosis_fee: 0 });
  const [selectedIds, setSelectedIds] = useState(params.get("service") ? [params.get("service")] : []);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotMsg, setSlotMsg] = useState("");
  const [form, setForm] = useState({
    customer_name: "", phone: "", address: "", car_brand: "", car_model: "",
    car_year: "", plate: "", problem_description: "", payment_method: "pix",
  });
  const [symptom, setSymptom] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/config/fees").then((r) => setFees(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) { setSlots([]); setSlotMsg(""); return; }
    setSlotsLoading(true);
    setTime("");
    api.get(`/availability/slots?date=${toYMD(date)}`)
      .then((r) => {
        setSlots(r.data.slots || []);
        if (r.data.blocked) setSlotMsg(r.data.reason ? `Data bloqueada: ${r.data.reason}` : "Data bloqueada pelo admin");
        else if ((r.data.slots || []).length === 0) setSlotMsg("Sem horários disponíveis neste dia");
        else setSlotMsg("");
      })
      .catch(() => { setSlots([]); setSlotMsg("Erro ao carregar horários"); })
      .finally(() => setSlotsLoading(false));
  }, [date]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", phone: f.phone || user.phone || "" }));
    }
  }, [user]);

  if (!loading && !user) {
    nav("/entrar", { state: { from: "/agendar" } });
    return null;
  }

  const chosen = services.filter((s) => selectedIds.includes(s.service_id));
  const servicesTotal = chosen.reduce((sum, s) => sum + Number(s.price), 0);
  const total = servicesTotal + Number(fees.travel_fee || 0);

  const toggleService = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Escolha data e horário"); return; }
    if (selectedIds.length === 0) { toast.error("Selecione pelo menos 1 serviço (ou apenas diagnóstico)"); return; }
    const scheduled = new Date(date);
    const [h, m] = time.split(":").map(Number);
    scheduled.setHours(h, m, 0, 0);
    const descFull = symptom ? `${symptom}${form.problem_description ? " — " + form.problem_description : ""}` : form.problem_description;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        service_ids: selectedIds,
        customer_name: form.customer_name,
        phone: form.phone,
        address: form.address,
        car_brand: form.car_brand,
        car_model: form.car_model,
        car_year: form.car_year,
        plate: form.plate,
        problem_description: descFull,
        scheduled_date: scheduled.toISOString(),
        payment_method: form.payment_method,
      });
      toast.success("Agendamento criado com sucesso!");
      nav("/painel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao agendar");
    } finally { setSubmitting(false); }
  };

  const onC = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-3">Agendamento</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Agendar atendimento delivery</h1>
          <p className="mt-3 text-slate-300">Preencha os dados abaixo. O valor é confirmado antes da execução do serviço.</p>
        </div>

        <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-6">
            {/* Services */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Wrench className="w-5 h-5 text-sky-400" />
                  <h2 className="text-lg font-bold text-white">Serviços desejados</h2>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {services.map((s) => {
                    const checked = selectedIds.includes(s.service_id);
                    return (
                      <div
                        key={s.service_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleService(s.service_id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleService(s.service_id); } }}
                        data-testid={`booking-service-${s.service_id}`}
                        className={`text-left p-3 rounded-lg border transition-all cursor-pointer ${checked ? "border-sky-500 bg-sky-500/10" : "border-slate-700 hover:border-slate-600"}`}
                      >
                        <div className="flex items-start gap-2 pointer-events-none">
                          <Checkbox checked={checked} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white text-sm">{s.name}</div>
                            <div className="text-xs text-sky-400 font-mono mt-1">R$ {Number(s.price).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Date/Time */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarIcon className="w-5 h-5 text-sky-400" />
                  <h2 className="text-lg font-bold text-white">Data e horário</h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-slate-300">Data</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start mt-1.5 bg-slate-800 border-slate-700 text-white hover:bg-slate-700 h-11" data-testid="booking-date-trigger">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {date ? format(date, "PPP", { locale: ptBR }) : "Escolher data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} locale={ptBR} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-slate-300">Horário</Label>
                    {!date ? (
                      <div className="text-xs text-slate-500 mt-3 py-2">Escolha uma data para ver os horários</div>
                    ) : slotsLoading ? (
                      <div className="text-xs text-sky-400 mt-3 py-2 font-mono">Carregando...</div>
                    ) : slots.length === 0 ? (
                      <div className="text-xs text-yellow-400 mt-3 py-2" data-testid="no-slots-msg">{slotMsg || "Sem horários disponíveis"}</div>
                    ) : (
                      <div className="grid grid-cols-5 gap-1.5 mt-1.5">
                        {slots.map((t) => (
                          <button
                            type="button"
                            key={t}
                            onClick={() => setTime(t)}
                            data-testid={`time-${t}`}
                            className={`py-2 text-xs rounded font-mono ${time === t ? "bg-sky-500 text-slate-950 font-bold" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                          >{t}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact & Address */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-sky-400" />
                  <h2 className="text-lg font-bold text-white">Contato e endereço</h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-slate-300">Nome completo</Label>
                    <Input required value={form.customer_name} onChange={onC("customer_name")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-name" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Telefone / WhatsApp</Label>
                    <Input required value={form.phone} onChange={onC("phone")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-300">Endereço completo (com ponto de referência)</Label>
                    <Input required value={form.address} onChange={onC("address")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-address" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Car */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Car className="w-5 h-5 text-sky-400" />
                  <h2 className="text-lg font-bold text-white">Dados do veículo</h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-slate-300">Marca</Label>
                    <Input required value={form.car_brand} onChange={onC("car_brand")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-brand" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Modelo</Label>
                    <Input required value={form.car_model} onChange={onC("car_model")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-model" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Ano</Label>
                    <Input value={form.car_year} onChange={onC("car_year")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-year" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Placa (opcional)</Label>
                    <Input value={form.plate} onChange={onC("plate")} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="booking-plate" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-slate-300">Sintoma principal</Label>
                  <div className="grid gap-1.5 sm:grid-cols-3 mt-1.5">
                    {problems.map((p) => (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setSymptom(p)}
                        className={`text-xs py-2 px-3 rounded-lg border ${symptom === p ? "border-sky-500 bg-sky-500/10 text-sky-300" : "border-slate-700 text-slate-300 hover:border-slate-600"}`}
                      >{p}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-slate-300">Descrição detalhada</Label>
                  <Textarea value={form.problem_description} onChange={onC("problem_description")} rows={3} className="bg-slate-800 border-slate-700 text-white mt-1.5" data-testid="booking-description" />
                </div>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-sky-400" />
                  <h2 className="text-lg font-bold text-white">Pagamento no local</h2>
                </div>
                <RadioGroup value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })} className="grid gap-2 sm:grid-cols-3">
                  {[
                    { v: "pix", l: "PIX" },
                    { v: "card", l: "Cartão (maquininha)" },
                    { v: "cash", l: "Dinheiro" },
                  ].map((o) => (
                    <label key={o.v} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.payment_method === o.v ? "border-sky-500 bg-sky-500/10" : "border-slate-700"}`}>
                      <RadioGroupItem value={o.v} data-testid={`pay-${o.v}`} />
                      <span className="text-white font-medium text-sm">{o.l}</span>
                    </label>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div>
            <div className="sticky top-20">
              <Card className="bg-gradient-to-br from-slate-900 to-slate-900/50 border-sky-500/30">
                <CardContent className="p-6">
                  <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-4">Resumo</div>
                  <div className="space-y-3">
                    {chosen.length === 0 ? (
                      <div className="text-sm text-slate-400">Nenhum serviço selecionado ainda.</div>
                    ) : chosen.map((s) => (
                      <div key={s.service_id} className="flex justify-between text-sm">
                        <span className="text-slate-300">{s.name}</span>
                        <span className="text-white font-mono">R$ {Number(s.price).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-3 border-t border-slate-800">
                      <span className="text-slate-400">Taxa de deslocamento</span>
                      <span className="text-sky-400 font-mono">R$ {Number(fees.travel_fee).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-slate-800">
                      <span className="text-white font-bold">Total estimado</span>
                      <span className="text-2xl font-black text-sky-400 font-mono" data-testid="booking-total">R$ {total.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-200 leading-relaxed">
                    <strong>Atenção:</strong> caso o orçamento não seja aprovado após o diagnóstico, será cobrada apenas a taxa de diagnóstico de <strong>R$ {Number(fees.diagnosis_fee).toFixed(2)}</strong> + deslocamento.
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full mt-5 bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold h-12 btn-shine" data-testid="booking-submit">
                    {submitting ? "Enviando..." : (<><CheckCircle2 className="w-4 h-4 mr-2" />Confirmar agendamento</>)}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}
